import { Header, Language } from '@innovationnorway/component-library';
import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import fbLogo from 'src/assets/img/nic_klynger_logo_positiv_nic-main.svg';
import { Link, useNavigate } from 'react-router-dom';
import type { ModuleTypes } from 'src/features/authorization';
import { useAuth, useUser } from 'src/features/authorization';
import { useCluster } from 'src/features/cluster';
import { useOneWayInNewOpportunitiesNumber } from 'src/features/one-way-in';
import useStatusPrograms from 'src/hooks/use-status-programs';
import i18n from 'src/i18n';
import { ACCOUNT_SETTINGS_BASE_ROUTE, CLUSTER_CHOOSER_BASE_ROUTE, ROUTES } from 'src/navigation';
import { useLocalStorage } from 'usehooks-ts';
import { AppLanguages, MenuItem, NavItem } from './types';
import useNavItemModules from 'src/hooks/use-nav-item-modules';
import useSiteimprove from 'src/hooks/use-siteimprove';
import { SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';
import './Navbar.scss';
import { toast } from 'react-toastify';
import { Dialog, Heading, Select } from '@innovationnorway/cl-react';

const Navbar: React.FC = () => {
  const { t: tCommon } = useTranslation();
  const { t: tPageTitles } = useTranslation('pageTitles');
  const { NavItemModules } = useNavItemModules();

  const { cluster } = useCluster();
  const { checkAccess } = useAuth();

  const { displayName, isUserLoggedIn, logOut, downloadSwagger } = useUser();
  const { alertOneWayInNewOpportunities } = useOneWayInNewOpportunitiesNumber();
  const { isProgramOneWayIn, isProgramCluster } = useStatusPrograms();

  const [showLanguagePicker, setShowLanguagePicker] = useState(false);

  const navigate = useNavigate();
  const siteimprove = useSiteimprove();

  useEffect(() => {
    if (alertOneWayInNewOpportunities === 0 || cluster?.oneWayInVmaNames.length !== 0) return;
    const navbar = document.getElementById('app-navbar');
    if (navbar === null) return;
    const links = Array.from(navbar.getElementsByTagName('a'));
    const notification = document.createElement('div');
    notification.classList.add('notification--bubble');
    notification.textContent = alertOneWayInNewOpportunities.toString();
    const opportunityLink = links.filter((link) => link.pathname === ROUTES.ONE_WAY_IN.BASE)[0];
    opportunityLink.after(notification);
    return () => {
      if (notification !== null) notification.remove();
    };
  }, [alertOneWayInNewOpportunities, cluster?.oneWayInVmaNames.length]);

  const handleOnClick = useCallback(
    (href: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      navigate(href);
      siteimprove.log(SITEIMPROVE_DECLARATIONS.NAVIGATION.id, href)
    },
    [navigate, siteimprove],
  );

  const navItemList = useMemo(() => {
    const navItems: NavItem[] = [];

    for (const item of NavItemModules) {
      if (
        // No module check
        item.module === undefined ||
        // If it's an array, make sure we pass at least one
        (Array.isArray(item.module) &&
          item.module.map((module) => checkAccess(module).read).includes(true)) ||
        // If it's only one, make sure we pass
        checkAccess(item.module as ModuleTypes).read === true
      ) {
        // Hvis "SivaCore" og bruker har tilgang til "SivaAdmin", ikke vis "SivaCore"
        if (item.module === 'SivaCore' && checkAccess('SivaAdmin').read === true) {
          continue;
        }

        navItems.push({
          text: tPageTitles(item.text),
          active: location.pathname.startsWith(item.path) && item.path !== '/',
          href: item.path,
          onClick: handleOnClick(item.path),
        });
      }
    }

    return navItems;
  }, [NavItemModules, checkAccess, handleOnClick, tPageTitles]);

  const menuItemList = useMemo(() => {
    const menuItems: MenuItem[] = [];

    if (checkAccess('AccountSettings').read && !isProgramCluster) {
      menuItems.push(
        {
          text: tPageTitles('myContactInformation'),
          href: ROUTES.ACTOR_PROFILE.SETTINGS.CONTACT_INFORMATION,
        },
        {
          text: tPageTitles('mailSettings'),
          href: ROUTES.ACTOR_PROFILE.SETTINGS.EMAIL,
        },
        {
          text: tPageTitles('accessControls'),
          href: ROUTES.ACTOR_PROFILE.SETTINGS.ACCESS_CONTROL,
        },
        {
          text: tPageTitles('actorProfile'),
          href: ROUTES.ACTOR_PROFILE.BASE,
        },
      );
    }

    if (checkAccess('AccountSettings').read && isProgramCluster) {
      menuItems.push({
        text: tPageTitles('settings'),
        href: ACCOUNT_SETTINGS_BASE_ROUTE,
      });
    }
    if (process.env.NODE_ENV !== 'production') {
      menuItems.push({
        text: 'DEV: Swagger',
        href: '#',
        onClick(e) {
          e.preventDefault();
          downloadSwagger();
          toast.success('Lastet ned swagger.json fra ' + process.env.NODE_ENV);
        },
      });
    }
    // Viser alltid denne
    menuItems.push({
      text: tCommon('chooseActor'),
      href: CLUSTER_CHOOSER_BASE_ROUTE,
    });

    if (isUserLoggedIn) {
      menuItems.push({
        text: tCommon('logOut'),
        onClick: logOut,
      });
    }

    return menuItems;
  }, [checkAccess, isProgramCluster, tCommon, isUserLoggedIn, tPageTitles, downloadSwagger, logOut]);

  // Språk
  const [selectedLanguage, setSelectedLanguage] = useLocalStorage<AppLanguages>('lang', 'nb');

  const selectLanguageHandler = useCallback(
    (lang: AppLanguages) => {
      switch (lang) {
        case 'nb':
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.CHANGE_LANGUAGE.id,
            SITEIMPROVE_DECLARATIONS.CHANGE_LANGUAGE.actions.CHANGE_TO_NB,
            displayName,
          );
          break;
        case 'nn':
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.CHANGE_LANGUAGE.id,
            SITEIMPROVE_DECLARATIONS.CHANGE_LANGUAGE.actions.CHANGE_TO_NN,
            displayName,
          );
          break;
        case 'en':
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.CHANGE_LANGUAGE.id,
            SITEIMPROVE_DECLARATIONS.CHANGE_LANGUAGE.actions.CHANGE_TO_EN,
            displayName,
          );
          break;
      }
      i18n.changeLanguage(lang);
      setSelectedLanguage(lang);
      document.documentElement.lang = lang;
    },
    [displayName, setSelectedLanguage, siteimprove],
  );

  const languages = useMemo(() => {
    const languages: Language[] = [
      {
        code: 'nb',
        isSelected: selectedLanguage === 'nb',
        name: 'Bokmål',
        onClick: () => selectLanguageHandler('nb'),
      },
      {
        code: 'nn',
        isSelected: selectedLanguage === 'nn',
        name: 'Nynorsk',
        onClick: () => selectLanguageHandler('nn'),
      },
      {
        code: 'en',
        isSelected: selectedLanguage === 'en',
        name: 'English',
        onClick: () => selectLanguageHandler('en'),
      },
    ];

    return languages;
  }, [selectLanguageHandler, selectedLanguage]);

  const { logoImgSrc, customLogoImgSrc } = useMemo(() => {
    if (isProgramOneWayIn) {
      return {
        logoImgSrc: undefined,
        customLogoImgSrc: undefined,
      };
    }

    return {
      logoImgSrc: fbLogo,
      customLogoImgSrc: fbLogo,
    };
  }, [isProgramOneWayIn]);

  if (!isUserLoggedIn) {
    return null;
  }

  return (
    <>
      <Header
        id="app-navbar"
        // Brukerinfo
        name={displayName || ''}
        companyName={cluster?.name || ''}
        hotJarClass="data-hj-suppress"
        // Hovedmeny
        navItems={navItemList}
        navAriaLabel={tCommon('mainMenu')}
        // Brukermeny
        menuItems={menuItemList}
        // Språkvelger
        languages={languages}
        languagesLabel={`${tCommon('language')}:`}
        // Logo
        logoLink="/"
        logoImgSrc={logoImgSrc}
        logoAriaLabel={tCommon('toFrontpage')}
        customLogo={
          customLogoImgSrc ? (
            <Link to={'/'}>
              <img src={customLogoImgSrc} alt={tCommon('toFrontpage')} />
            </Link>
          ) : undefined
        }
        // Styling
        className="margin-bottom--0 app-navbar navbar__fix"
      />
      <Dialog open={showLanguagePicker} onClose={() => setShowLanguagePicker(false)}>
        <div className="stack-small">
          <Heading>{tCommon('selectLanguage')}</Heading>
          <Select
            onChange={(e) => {
              selectLanguageHandler(e.currentTarget.value as AppLanguages);
              setShowLanguagePicker(false);
            }}
            value={selectedLanguage}
          >
            {languages.map((lang) => (
              <Select.Option key={lang.code + '_lang'} value={lang.code}>
                {lang.name}
              </Select.Option>
            ))}
          </Select>
        </div>
      </Dialog>
    </>
  );
};

export default Navbar;
